import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Building2, ClipboardList, Sparkles } from 'lucide-react'
import { PelajariMascot } from './PelajariMascot'

gsap.registerPlugin(ScrollTrigger)

// ─── Step 1 Mockup: Set up your organization ──────────────────────────────────

function OrgSetupMockup() {
  const divisions = [
    { name: 'Engineering', headcount: 12, color: '#359d9e' },
    { name: 'Marketing', headcount: 7, color: '#27777a' },
    { name: 'HR & People', headcount: 4, color: '#368fc1' },
  ]
  const newMembers = [
    { initials: 'AB', name: 'Andi Budiman', role: 'Sr. Engineer', division: 'Engineering' },
    { initials: 'SR', name: 'Siti Rahayu', role: 'Marketing Lead', division: 'Marketing' },
  ]

  return (
    <div className="w-full rounded-xl border border-[rgba(53,157,158,0.15)] bg-white shadow-[0px_4px_24px_0px_rgba(53,157,158,0.12)] overflow-hidden">
      {/* Header bar */}
      <div className="bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)] px-4 py-3 flex items-center justify-between">
        <span className="text-xs font-bold text-[#1a2727]">Organization Setup</span>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#ff5f57]" />
          <div className="w-2 h-2 rounded-full bg-[#febc2e]" />
          <div className="w-2 h-2 rounded-full bg-[#28c840]" />
        </div>
      </div>

      <div className="p-4 flex flex-col gap-4">
        {/* Org chart mini-view */}
        <div>
          <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2.5">Divisions</p>
          <div className="space-y-2">
            {divisions.map((d) => (
              <div key={d.name} className="flex items-center gap-3">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: d.color }}
                />
                <div className="flex-1 h-2 rounded-full bg-[#f2f3f3] overflow-hidden">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${(d.headcount / 16) * 100}%`,
                      backgroundColor: d.color,
                      opacity: 0.7,
                    }}
                  />
                </div>
                <span className="text-[10px] font-medium text-[#1a2727] w-24 truncate">{d.name}</span>
                <span className="text-[10px] text-[#b3b7b7] flex-shrink-0">{d.headcount} people</span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[rgba(53,157,158,0.08)]" />

        {/* New invites */}
        <div>
          <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2.5">Recently Invited</p>
          <div className="space-y-2">
            {newMembers.map((m) => (
              <div key={m.initials} className="flex items-center gap-2.5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: '#359d9e' }}
                >
                  {m.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-[#1a2727] truncate">{m.name}</p>
                  <p className="text-[9px] text-[#b3b7b7] truncate">{m.role} · {m.division}</p>
                </div>
                <div className="bg-[#f2fbfa] border border-[#d5f2ef] text-[#27777a] text-[8px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0">
                  Active
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Org chart hint */}
        <div className="bg-[#f2fbfa] rounded-lg px-3 py-2 flex items-center gap-2">
          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
            <circle cx="8" cy="3" r="2" stroke="#359d9e" strokeWidth="1.2" />
            <circle cx="3" cy="11" r="2" stroke="#359d9e" strokeWidth="1.2" />
            <circle cx="13" cy="11" r="2" stroke="#359d9e" strokeWidth="1.2" />
            <path d="M8 5V8M8 8L3 9M8 8L13 9" stroke="#359d9e" strokeWidth="1" />
          </svg>
          <p className="text-[9px] text-[#27777a]">Org chart auto-updates as you add people</p>
        </div>
      </div>
    </div>
  )
}

// ─── Step 2 Mockup: Assess and track performance ──────────────────────────────

function AssessPerformanceMockup() {
  const sections = [
    { title: 'Leadership', rating: 4, color: '#359d9e' },
    { title: 'Delivery', rating: 5, color: '#27777a' },
    { title: 'Collaboration', rating: 3, color: '#359d9e' },
    { title: 'Initiative', rating: 4, color: '#27777a' },
  ]

  const scaleLabels: Record<number, string> = {
    1: 'Needs Work', 2: 'Below', 3: 'Meets', 4: 'Exceeds', 5: 'Outstanding',
  }

  return (
    <div className="w-full rounded-xl border border-[rgba(53,157,158,0.15)] bg-white shadow-[0px_4px_24px_0px_rgba(53,157,158,0.12)] overflow-hidden">
      {/* Header bar */}
      <div className="bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)] px-4 py-3 flex items-center justify-between">
        <span className="text-xs font-bold text-[#1a2727]">Q1 Performance Review</span>
        <div className="bg-[#fff7ed] text-[#fc8213] text-[9px] font-semibold px-2 py-0.5 rounded-full">
          In Progress
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3">
        {/* Psychometric badge */}
        <div className="flex items-center gap-2 bg-[#f2fbfa] rounded-lg px-3 py-2">
          <div className="w-5 h-5 rounded-md bg-[#d5f2ef] flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
              <path d="M2 9L4.5 4L6.5 7L8 5.5L10 9H2Z" fill="#359d9e" opacity="0.4" />
              <path d="M2 9L4.5 4L6.5 7L8 5.5L10 9" stroke="#359d9e" strokeWidth="1" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-semibold text-[#27777a]">Big Five Psychometric</p>
            <p className="text-[8px] text-[#b3b7b7]">Completed · Score: 82/100</p>
          </div>
          <svg viewBox="0 0 12 12" className="w-3.5 h-3.5 text-[#359d9e] flex-shrink-0" fill="none">
            <circle cx="6" cy="6" r="5" stroke="#359d9e" strokeWidth="1.2" />
            <path d="M4 6L5.5 7.5L8 4.5" stroke="#359d9e" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Section ratings */}
        <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider">Competency Ratings</p>
        <div className="space-y-2.5">
          {sections.map((s) => (
            <div key={s.title}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-[#1a2727]">{s.title}</span>
                <span className="text-[9px] font-semibold" style={{ color: s.color }}>
                  {s.rating}/5 · {scaleLabels[s.rating]}
                </span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((v) => (
                  <div
                    key={v}
                    className="flex-1 h-1.5 rounded-full"
                    style={{
                      backgroundColor: v <= s.rating ? s.color : '#e6e7e7',
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Submit button */}
        <div className="mt-1 flex items-center justify-between">
          <p className="text-[9px] text-[#b3b7b7]">2 sections remaining</p>
          <div className="bg-[#359d9e] text-white text-[9px] font-semibold px-3 py-1 rounded-md">
            Submit Review
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Step 3 Mockup: Turn data into decisions ──────────────────────────────────

function DataDecisionsMockup() {
  // 3×3 nine-box grid
  const cells = [
    [false, false, true],
    [false, true, false],
    [true, false, false],
  ]

  const report = [
    { label: 'Potential', value: 'High', color: '#359d9e' },
    { label: 'Performance', value: 'Above Avg', color: '#27777a' },
    { label: 'Flight Risk', value: 'Low', color: '#28c840' },
  ]

  const idpGoals = [
    { label: 'Leadership Skills', pct: 65 },
    { label: 'Public Speaking', pct: 40 },
  ]

  return (
    <div className="w-full rounded-xl border border-[rgba(53,157,158,0.15)] bg-white shadow-[0px_4px_24px_0px_rgba(53,157,158,0.12)] overflow-hidden">
      {/* Header bar */}
      <div className="bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)] px-4 py-3 flex items-center justify-between">
        <span className="text-xs font-bold text-[#1a2727]">Talent Intelligence</span>
        <div className="flex items-center gap-1 text-[9px] text-[#359d9e] font-semibold">
          <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
            <path d="M6 1L7.5 4.5H11L8 6.5L9 10L6 8L3 10L4 6.5L1 4.5H4.5L6 1Z" fill="#359d9e" />
          </svg>
          AI-powered
        </div>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="flex gap-4">
          {/* 9-Box Grid */}
          <div className="flex-shrink-0">
            <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2">9-Box Grid</p>
            <div className="grid grid-cols-3 gap-1 w-[90px]">
              {cells.map((row, ri) =>
                row.map((active, ci) => (
                  <div
                    key={`${ri}-${ci}`}
                    className="h-7 rounded flex items-center justify-center"
                    style={{
                      backgroundColor: active ? '#359d9e' : ri === 0 && ci === 1 ? '#d5f2ef' : '#f2f3f3',
                    }}
                  >
                    {active && (
                      <div className="w-3 h-3 rounded-full bg-white border-[1.5px] border-[#27777a]" />
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-[8px] text-[#b3b7b7] mt-1.5 text-center w-[90px]">Hana · Star Player</p>
          </div>

          {/* AI Report pills */}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2">AI Talent Report</p>
            <div className="space-y-1.5">
              {report.map((r) => (
                <div key={r.label} className="flex items-center justify-between gap-2">
                  <span className="text-[9px] text-[#1a2727]">{r.label}</span>
                  <span
                    className="text-[9px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: `${r.color}18`, color: r.color }}
                  >
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[rgba(53,157,158,0.08)]" />

        {/* IDP */}
        <div>
          <p className="text-[10px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2">Development Plan (IDP)</p>
          <div className="space-y-2">
            {idpGoals.map((g) => (
              <div key={g.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-[#1a2727]">{g.label}</span>
                  <span className="text-[9px] font-semibold text-[#359d9e]">{g.pct}%</span>
                </div>
                <div className="h-1.5 bg-[#e6e7e7] rounded-full overflow-hidden">
                  <div className="h-1.5 bg-[#359d9e] rounded-full" style={{ width: `${g.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recruit hint */}
        <div className="bg-[#f2fbfa] rounded-lg px-3 py-2 flex items-center gap-2">
          <svg viewBox="0 0 14 14" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
            <circle cx="5" cy="4" r="2.5" stroke="#359d9e" strokeWidth="1.1" />
            <path d="M1 12C1 9.79 2.79 8 5 8C7.21 8 9 9.79 9 12" stroke="#359d9e" strokeWidth="1.1" strokeLinecap="round" />
            <path d="M10 5L12 7L10 9" stroke="#fc8213" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-[9px] text-[#27777a]">2 open roles matched to competency gaps</p>
        </div>
      </div>
    </div>
  )
}

// ─── Step definitions ─────────────────────────────────────────────────────────

const steps = [
  {
    title: 'Set up your organization',
    description: 'Invite employees, define divisions, positions, and org levels. Your org chart builds itself automatically as you grow.',
    Icon: Building2,
    imagePosition: 'left' as const,
    Mockup: OrgSetupMockup,
  },
  {
    title: 'Assess and track performance',
    description: 'Assign psychometric tests, run performance assessment sessions, and track competency scores across your entire team in real time.',
    Icon: ClipboardList,
    imagePosition: 'right' as const,
    Mockup: AssessPerformanceMockup,
  },
  {
    title: 'Turn data into decisions',
    description: 'View your 9-Box grid, generate AI-powered talent reports, recruit smarter with competency-matched candidates, and grow your people with structured IDPs.',
    Icon: Sparkles,
    imagePosition: 'left' as const,
    Mockup: DataDecisionsMockup,
  },
]

// ─── Main Section ─────────────────────────────────────────────────────────────

export function LandingHowItWorks() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const lineRef = useRef<HTMLDivElement>(null)
  const mobileLineRef = useRef<HTMLDivElement>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const iconRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header reveal
      gsap.set(headerRef.current, { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: headerRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(headerRef.current, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out' }),
      })

      // Timeline line draw (desktop)
      if (lineRef.current) {
        gsap.set(lineRef.current, { scaleY: 0, transformOrigin: 'top center' })
        ScrollTrigger.create({
          trigger: lineRef.current,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 0.8,
          onUpdate: (self) => {
            gsap.set(lineRef.current, { scaleY: self.progress })
          },
        })
      }

      // Timeline line draw (mobile)
      if (mobileLineRef.current) {
        gsap.set(mobileLineRef.current, { scaleY: 0, transformOrigin: 'top center' })
        ScrollTrigger.create({
          trigger: mobileLineRef.current,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 0.8,
          onUpdate: (self) => {
            gsap.set(mobileLineRef.current, { scaleY: self.progress })
          },
        })
      }

      // Step content + icon nodes
      stepRefs.current.forEach((step, i) => {
        if (!step) return
        gsap.set(step, { opacity: 0, y: 24 })
        ScrollTrigger.create({
          trigger: step,
          start: 'top 82%',
          once: true,
          onEnter: () => gsap.to(step, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: i * 0.1 }),
        })
      })

      iconRefs.current.forEach((icon, i) => {
        if (!icon) return
        gsap.set(icon, { scale: 0, opacity: 0 })
        ScrollTrigger.create({
          trigger: icon,
          start: 'top 85%',
          once: true,
          onEnter: () => gsap.to(icon, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.7)', delay: i * 0.1 }),
        })
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="how-it-works" className="flex flex-col items-center gap-16 px-6 py-16 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
      {/* Header */}
      <div ref={headerRef} className="flex flex-col items-center gap-6 max-w-[979px] text-center">
        <span className="inline-flex items-center gap-2 bg-[#f2fbfa] border border-[#359d9e] text-[#359d9e] px-4 py-2 rounded-lg text-sm font-medium sm:text-base">
          <PelajariMascot className="w-5 h-4 flex-shrink-0" />
          How Pelajari Works
        </span>
        <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-[#000f0f] sm:text-[40px] sm:leading-[48px] lg:text-[48px] lg:leading-[58px] lg:tracking-[-1px]">
          Start their growth journey in 3 simple steps
        </h2>
      </div>

      {/* Timeline */}
      <div className="w-full max-w-[1200px]">

        {/* ── Desktop layout (lg+): alternating left/right with centered icon column ── */}
        <div className="hidden lg:block relative">
          {/* Continuous vertical line through the center column */}
          <div ref={lineRef} className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[#359d9e] opacity-30" />

          {steps.map((step, index) => {
            const isLeftImage = step.imagePosition === 'left'
            const { Icon, Mockup } = step
            return (
              <div
                key={step.title}
                ref={(el) => { stepRefs.current[index] = el }}
                className="relative grid grid-cols-[1fr_120px_1fr] items-center gap-0 py-14"
              >
                {/* Left slot */}
                {isLeftImage ? (
                  <div className="pr-10 flex justify-end">
                    <div className="w-full max-w-[420px]">
                      <Mockup />
                    </div>
                  </div>
                ) : (
                  <div className="pr-10 flex flex-col justify-center text-right">
                    <span className="text-xs font-bold text-[#359d9e] uppercase tracking-widest mb-2">Step {index + 1}</span>
                    <h3 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#27777a] lg:text-[32px] lg:leading-[40px]">
                      {step.title}
                    </h3>
                    <p className="text-base text-[#1a2727] mt-3">{step.description}</p>
                  </div>
                )}

                {/* Center: icon node on the line */}
                <div className="flex justify-center relative z-10">
                  <div
                    ref={(el) => { iconRefs.current[index] = el }}
                    className="w-[72px] h-[72px] bg-white rounded-full shadow-[0px_4px_24px_0px_rgba(53,157,158,0.2)] border-2 border-[#359d9e]/20 flex items-center justify-center"
                  >
                    <Icon className="w-8 h-8 text-[#359d9e]" />
                  </div>
                </div>

                {/* Right slot */}
                {!isLeftImage ? (
                  <div className="pl-10 flex justify-start">
                    <div className="w-full max-w-[420px]">
                      <Mockup />
                    </div>
                  </div>
                ) : (
                  <div className="pl-10 flex flex-col justify-center">
                    <span className="text-xs font-bold text-[#359d9e] uppercase tracking-widest mb-2">Step {index + 1}</span>
                    <h3 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#27777a] lg:text-[32px] lg:leading-[40px]">
                      {step.title}
                    </h3>
                    <p className="text-base text-[#1a2727] mt-3">{step.description}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Mobile / tablet layout (below lg): vertical stack with left timeline ── */}
        <div className="lg:hidden relative pl-10">
          {/* Continuous vertical line on the left */}
          <div ref={mobileLineRef} className="absolute left-[19px] top-3 bottom-3 w-px bg-[#359d9e] opacity-30" />

          {steps.map((step, index) => {
            const { Icon, Mockup } = step
            return (
              <div key={step.title} className="relative flex gap-6 pb-12 last:pb-0">
                {/* Icon node */}
                <div className="absolute -left-10 top-0 w-10 h-10 bg-white rounded-full shadow-[0px_4px_16px_0px_rgba(53,157,158,0.2)] border-2 border-[#359d9e]/20 flex items-center justify-center z-10 flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#359d9e]" />
                </div>

                {/* Content */}
                <div className="flex flex-col gap-4 w-full">
                  <div>
                    <span className="text-xs font-bold text-[#359d9e] uppercase tracking-widest">Step {index + 1}</span>
                    <h3 className="text-[24px] font-semibold leading-[32px] tracking-[-0.5px] text-[#27777a] mt-1 sm:text-[28px] sm:leading-[36px]">
                      {step.title}
                    </h3>
                    <p className="text-base text-[#1a2727] mt-2">{step.description}</p>
                  </div>
                  <Mockup />
                </div>
              </div>
            )
          })}
        </div>

      </div>
    </section>
  )
}
